import React, { useState, useEffect, useRef, useCallback } from "react";
import { QRCodeSVG } from "qrcode.react";

const API_BASE = process.env.REACT_APP_API_URL || "";

const T = {
  rose: "#e11d48", roseDeep: "#be123c", roseSoft: "#fff1f2",
  violet: "#7c3aed", teal: "#0d9488", amber: "#d97706",
  text: "#1a1a1a", muted: "#6b7280", border: "#e5e7eb",
  card: "#ffffff", bg: "#f5f7fa",
};

// ── Helpers ────────────────────────────────────────────────────────
function getToken() {
  return localStorage.getItem("soul_session_token");
}

async function apiCreateShareLink({ type, platform, config, label }) {
  const res = await fetch(`${API_BASE}/api/share/create`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
    body: JSON.stringify({ type, platform, config, label }),
  });
  if (!res.ok) throw new Error("Failed to create share link");
  return res.json();
}

export async function apiFetchShareLinks() {
  const res = await fetch(`${API_BASE}/api/user/share-links`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!res.ok) throw new Error("Failed to fetch share links");
  return res.json();
}

export async function apiToggleShareLink(id, is_active) {
  const res = await fetch(`${API_BASE}/api/share/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
    body: JSON.stringify({ is_active }),
  });
  if (!res.ok) throw new Error("Failed to update");
  return res.json();
}

export async function apiDeleteShareLink(id) {
  const res = await fetch(`${API_BASE}/api/share/${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!res.ok) throw new Error("Failed to delete");
  return res.json();
}

export async function apiUpdateShareLinkLabel(id, label) {
  const res = await fetch(`${API_BASE}/api/share/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
    body: JSON.stringify({ label }),
  });
  if (!res.ok) throw new Error("Failed to update label");
  return res.json();
}

// ── Social share URL builders ──────────────────────────────────────
function buildSocialUrl(platform, shareUrl, text) {
  const enc = encodeURIComponent;
  switch (platform) {
    case "twitter":  return `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(shareUrl)}`;
    case "whatsapp": return `https://api.whatsapp.com/send?text=${enc(text + " " + shareUrl)}`;
    case "facebook": return `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`;
    case "threads":  return `https://www.threads.net/intent/post?text=${enc(text + " " + shareUrl)}`;
    case "telegram": return `https://t.me/share/url?url=${enc(shareUrl)}&text=${enc(text)}`;
    default: return null;
  }
}

const PLATFORM_META = {
  contact:  { label: "Contacts",    bg: "linear-gradient(135deg,#e11d48,#be123c)" },
  qr:       { label: "QR Code",     bg: "#1a1a1a" },
  link:     { label: "Copy Link",   bg: "#0d9488" },
  twitter:  { label: "X / Twitter", bg: "#000000" },
  whatsapp: { label: "WhatsApp",    bg: "#25D366" },
  facebook: { label: "Facebook",    bg: "#1877F2" },
  threads:  { label: "Threads",     bg: "#101010" },
  telegram: { label: "Telegram",    bg: "#229ED9" },
};

// Keep alias so older references still work
const PLATFORM_ICONS = PLATFORM_META;

// ── Settup logo data-URI for QR center ─────────────────────────────
const SETTUP_QR_LOGO = `data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40">' +
  '<rect width="40" height="40" rx="8" fill="#e11d48"/>' +
  '<text x="20" y="29" text-anchor="middle" font-family="Georgia,serif" font-size="27" font-weight="bold" fill="white">s</text>' +
  '</svg>'
)}`;

// ── SVG brand icons ─────────────────────────────────────────────────
function PlatformIcon({ platform, size = 38 }) {
  const r = size / 2;
  const ic = Math.round(size * 0.46);
  const bg = PLATFORM_META[platform]?.bg || "#888";
  const circle = (children) => (
    <div style={{ width: size, height: size, borderRadius: "50%", background: bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      {children}
    </div>
  );
  switch (platform) {
    case "contact": return circle(
      <svg width={ic} height={ic} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16,6 12,2 8,6"/><line x1="12" y1="2" x2="12" y2="15"/>
      </svg>
    );
    case "qr": return circle(
      <svg width={ic} height={ic} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8">
        <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
        <rect x="3" y="14" width="7" height="7" rx="1"/>
        <rect x="5" y="5" width="3" height="3" fill="#fff" stroke="none"/><rect x="16" y="5" width="3" height="3" fill="#fff" stroke="none"/>
        <rect x="5" y="16" width="3" height="3" fill="#fff" stroke="none"/>
        <path d="M14 14h3v3m3 1v3m-3 0h3" strokeLinecap="round"/>
      </svg>
    );
    case "link": return circle(
      <svg width={ic} height={ic} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
      </svg>
    );
    case "twitter": return circle(
      <svg width={Math.round(ic * 0.9)} height={Math.round(ic * 0.9)} viewBox="0 0 24 24" fill="#fff">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.734l7.727-8.835L1.254 2.25H8.08l4.259 5.631 5.905-5.631zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    );
    case "whatsapp": return circle(
      <svg width={ic} height={ic} viewBox="0 0 24 24" fill="#fff">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
      </svg>
    );
    case "facebook": return circle(
      <svg width={ic} height={ic} viewBox="0 0 24 24" fill="#fff">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    );
    case "threads": return circle(
      <svg width={Math.round(ic * 0.85)} height={Math.round(ic * 0.85)} viewBox="0 0 192 192" fill="#fff">
        <path d="M141.537 88.988c-1.463-.676-2.964-1.298-4.499-1.862-1.494-27.307-16.415-42.94-41.469-43.1h-.315c-14.986 0-27.449 6.397-35.12 18.037l13.78 9.452c5.73-8.695 14.724-10.548 21.347-10.548.077 0 .153 0 .229.001 8.249.053 14.474 2.451 18.503 7.128 2.932 3.406 4.893 8.11 5.864 14.049-7.314-1.243-15.224-1.625-23.68-1.14-23.82 1.372-39.134 15.265-38.105 34.569.52 9.791 5.398 18.215 13.733 23.718 7.047 4.652 16.124 6.927 25.557 6.412 12.458-.683 22.231-5.436 29.049-14.127 5.178-6.6 8.453-15.153 9.899-25.93 5.937 3.583 10.337 8.298 12.767 13.966 4.132 9.635 4.373 25.468-8.546 38.376-11.319 11.308-24.925 16.2-45.488 16.351-22.81-.169-40.06-7.484-51.276-21.742C35.236 139.966 29.808 120.682 29.605 96c.203-24.682 5.631-43.966 16.133-57.317C56.954 24.425 74.204 17.11 97.013 16.94c22.975.171 40.526 7.521 52.171 21.848 5.71 7.025 10.015 15.86 12.853 26.162l16.147-4.308c-3.44-12.68-8.853-23.606-16.219-32.668C147.036 9.607 125.202.195 97.07 0h-.113C68.882.194 47.292 9.642 32.788 28.028 19.882 44.486 13.224 67.316 13.001 95.933L13 96l.001.068c.223 28.616 6.881 51.445 19.787 67.904C47.292 182.358 68.882 191.806 96.957 192h.113c24.96-.173 42.554-6.708 57.048-21.189 18.963-18.945 18.392-42.692 12.142-57.27-4.484-10.454-13.033-19.945-24.723-25.553zm-43.117 40.52c-10.44.588-21.286-4.098-21.821-14.135-.364-7.442 5.329-15.746 22.494-16.735 1.966-.113 3.895-.168 5.79-.168 6.235 0 12.068.606 17.371 1.766-1.978 24.702-13.528 28.714-23.834 29.272z"/>
      </svg>
    );
    case "telegram": return circle(
      <svg width={ic} height={ic} viewBox="0 0 24 24" fill="#fff">
        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
      </svg>
    );
    default: return circle(<span style={{ color: "#fff", fontSize: 14 }}>?</span>);
  }
}

// ── QR Code Modal ──────────────────────────────────────────────────
export function QRModal({ url, onClose, title }) {
  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
      onClick={onClose}
    >
      <div
        style={{ background: "#fff", borderRadius: 16, padding: 32, maxWidth: 340, width: "100%", textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ fontSize: 14, fontWeight: 700, color: T.rose, letterSpacing: "0.08em", marginBottom: 8 }}>SCAN TO JOIN</div>
        <div style={{ fontSize: 17, fontWeight: 700, color: T.text, marginBottom: 20 }}>{title}</div>
        <div style={{ display: "inline-block", padding: 12, background: "#fff", borderRadius: 12, border: `1px solid ${T.border}`, marginBottom: 20 }}>
          <QRCodeSVG
            value={url}
            size={200}
            level="H"
            imageSettings={{
              src: SETTUP_QR_LOGO,
              height: 40,
              width: 40,
              excavate: true,
            }}
          />
        </div>
        <div style={{ fontSize: 12, color: T.muted, marginBottom: 20, wordBreak: "break-all" }}>{url}</div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={() => { navigator.clipboard.writeText(url); }}
            style={{ flex: 1, padding: "10px 0", borderRadius: 8, background: T.roseSoft || "#fff1f2", border: `1px solid ${T.rose}40`, color: T.rose, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}
          >Copy Link</button>
          <button
            onClick={onClose}
            style={{ flex: 1, padding: "10px 0", borderRadius: 8, background: T.bg, border: `1px solid ${T.border}`, color: T.muted, fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}
          >Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Share Method Grid ──────────────────────────────────────────────
function ShareMethodGrid({ onSelect, type, appConnections = {} }) {
  const SOCIAL = ["twitter", "whatsapp", "facebook", "threads", "telegram"];
  const sortedSocial = [...SOCIAL].sort((a, b) => (appConnections[b] ? 1 : 0) - (appConnections[a] ? 1 : 0));
  const platforms = ["contact", "qr", "link", ...sortedSocial];
  const compatText = "Take a free compatibility test with me on settup!";
  const snapText = "Check out my personality snapshot on settup!";
  const shareText = type === "compatibility_test" ? compatText : snapText;

  return (
    <div style={{ padding: "16px 0 8px" }}>
      <div style={{ fontSize: 12, color: T.muted, fontWeight: 600, letterSpacing: "0.08em", marginBottom: 14, textAlign: "center" }}>
        SHARE VIA
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
        {platforms.map((p, i) => (
          <button
            key={p}
            onClick={() => onSelect(p, shareText)}
            style={{
              display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
              padding: "12px 8px", borderRadius: 12, border: `1px solid ${T.border}`,
              background: "#fff", cursor: "pointer", fontFamily: "inherit",
              animation: `emojiPop 0.35s ease ${i * 0.04}s both`,
              transition: "all 0.18s",
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = T.rose; e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.transform = "translateY(0)"; }}
          >
            <PlatformIcon platform={p} size={38} />
            <span style={{ fontSize: 10, color: T.muted, fontWeight: 600, textAlign: "center", lineHeight: 1.2 }}>
              {PLATFORM_META[p]?.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Share Flyout (dropdown on "Share with friends") ────────────────
export function ShareFlyout({ userAnswers, userContext, STAGES, SNAPSHOT_SECTIONS, getStageProgress, appConnections = {} }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("menu"); // "menu" | "compat_grid" | "snap_sheet" | "snap_grid" | "generating" | "done"
  const [selectedType, setSelectedType] = useState(null);
  const [shareLink, setShareLink] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const resetToMenu = () => {
    setView("menu");
    setSelectedType(null);
    setShareLink(null);
    setError(null);
    setCopied(false);
  };

  const handleSelectMethod = useCallback(async (platform, shareText, type, config) => {
    setView("generating");
    setError(null);
    try {
      const label = `${PLATFORM_META[platform]?.label || platform} — ${new Date().toLocaleDateString()}`;
      const link = await apiCreateShareLink({ type, platform, config: config || {}, label });
      setShareLink(link);

      if (platform === "contact") {
        if (navigator.share) {
          await navigator.share({ title: "Join me on settup", text: shareText, url: link.url }).catch(() => {});
        } else {
          navigator.clipboard.writeText(link.url).catch(() => {});
          setCopied(true);
        }
        setView("done");
      } else if (platform === "qr") {
        setView("done");
        setQrOpen(true);
      } else if (platform === "link") {
        navigator.clipboard.writeText(link.url).catch(() => {});
        setCopied(true);
        setView("done");
      } else {
        const socialUrl = buildSocialUrl(platform, link.url, shareText);
        if (socialUrl) window.open(socialUrl, "_blank", "noopener,noreferrer");
        setView("done");
      }
    } catch (err) {
      setError("Couldn't create link — check your connection.");
      setView("menu");
    }
  }, []);

  // Snapshot sheet state
  const [snapSheetOpen, setSnapSheetOpen] = useState(false);
  const [snapPlatform, setSnapPlatform] = useState(null);
  const [snapText, setSnapText] = useState("");

  const handleSnapMethodSelect = (platform, shareText) => {
    setSnapPlatform(platform);
    setSnapText(shareText);
    setSnapSheetOpen(true);
    setOpen(false);
  };

  const handleSnapConfirm = async (config) => {
    setSnapSheetOpen(false);
    setOpen(true);
    await handleSelectMethod(snapPlatform, snapText, "personality_snapshot", config);
  };

  return (
    <>
      <div ref={ref} style={{ position: "relative", display: "inline-flex" }}>
        {/* Trigger */}
        <div
          style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: T.muted, cursor: "pointer", padding: "4px 8px", borderRadius: 6, transition: "all 0.15s" }}
          onClick={() => { setOpen(o => !o); resetToMenu(); }}
          onMouseEnter={e => { e.currentTarget.style.color = T.rose; }}
          onMouseLeave={e => { e.currentTarget.style.color = T.muted; }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
          </svg>
          Share with friends
        </div>

        {/* Dropdown Panel */}
        {open && (
          <div style={{
            position: "absolute", top: "calc(100% + 8px)", right: 0, zIndex: 999,
            background: "#fff", borderRadius: 14, border: `1px solid ${T.border}`,
            boxShadow: "0 8px 32px rgba(0,0,0,0.14)", minWidth: 300, maxWidth: 340,
            animation: "reactionSlideUp 0.2s ease both", overflow: "hidden",
          }}>
            {/* Header */}
            <div style={{ padding: "14px 16px 10px", borderBottom: `1px solid ${T.border}` }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Share with friends</div>
              {view !== "menu" && (
                <button onClick={resetToMenu} style={{ fontSize: 11, color: T.rose, background: "none", border: "none", cursor: "pointer", padding: 0, marginTop: 2, fontFamily: "inherit" }}>← Back</button>
              )}
            </div>

            <div style={{ padding: "12px 16px 16px" }}>
              {/* Main menu */}
              {view === "menu" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <MenuOption
                    icon="⚡"
                    title="Test Compatibility"
                    desc="Share a link — friends sign up and see how compatible they are with you"
                    onClick={() => setView("compat_grid")}
                  />
                  <MenuOption
                    icon="🧬"
                    title="Share Personality Snapshot"
                    desc="Choose which stages of your personality to share publicly"
                    onClick={() => setView("snap_grid")}
                  />
                </div>
              )}

              {/* Compatibility test: pick platform */}
              {view === "compat_grid" && (
                <ShareMethodGrid
                  type="compatibility_test"
                  appConnections={appConnections}
                  onSelect={(platform, text) => handleSelectMethod(platform, text, "compatibility_test", {})}
                />
              )}

              {/* Snapshot: pick platform first, then configure stages in sheet */}
              {view === "snap_grid" && (
                <ShareMethodGrid
                  type="personality_snapshot"
                  appConnections={appConnections}
                  onSelect={handleSnapMethodSelect}
                />
              )}

              {/* Generating */}
              {view === "generating" && (
                <div style={{ textAlign: "center", padding: "24px 0" }}>
                  <div style={{ fontSize: 28, marginBottom: 12, animation: "orbPulse 1s ease-in-out infinite alternate" }}>🔗</div>
                  <div style={{ fontSize: 13, color: T.muted }}>Creating your share link…</div>
                </div>
              )}

              {/* Done */}
              {view === "done" && shareLink && (
                <div style={{ textAlign: "center", padding: "8px 0" }}>
                  <div style={{ fontSize: 28, marginBottom: 10 }}>✅</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 6 }}>
                    {copied ? "Link copied!" : "Shared!"}
                  </div>
                  <div style={{ fontSize: 12, color: T.muted, marginBottom: 14, wordBreak: "break-all" }}>
                    {shareLink.url}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      onClick={() => { navigator.clipboard.writeText(shareLink.url); setCopied(true); }}
                      style={{ flex: 1, padding: "8px 0", borderRadius: 8, background: "#fff1f2", border: `1px solid ${T.rose}40`, color: T.rose, fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}
                    >📋 Copy</button>
                    <button
                      onClick={() => setQrOpen(true)}
                      style={{ flex: 1, padding: "8px 0", borderRadius: 8, background: T.bg, border: `1px solid ${T.border}`, color: T.text, fontWeight: 600, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}
                    >⬛ QR Code</button>
                  </div>
                  <button onClick={resetToMenu} style={{ marginTop: 10, width: "100%", padding: "8px 0", borderRadius: 8, background: "none", border: `1px solid ${T.border}`, color: T.muted, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Share another</button>
                </div>
              )}

              {error && (
                <div style={{ fontSize: 12, color: "#ef4444", textAlign: "center", marginTop: 8 }}>{error}</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* QR Modal */}
      {qrOpen && shareLink && (
        <QRModal
          url={shareLink.url}
          title={selectedType === "compatibility_test" ? "Take a Compatibility Test" : "My Personality Snapshot"}
          onClose={() => setQrOpen(false)}
        />
      )}

      {/* Snapshot Sheet */}
      {snapSheetOpen && (
        <SnapshotShareSheet
          STAGES={STAGES}
          SNAPSHOT_SECTIONS={SNAPSHOT_SECTIONS}
          getStageProgress={getStageProgress}
          userContext={userContext}
          platform={snapPlatform}
          onConfirm={handleSnapConfirm}
          onClose={() => setSnapSheetOpen(false)}
        />
      )}
    </>
  );
}

// ── Menu Option row ────────────────────────────────────────────────
function MenuOption({ icon, title, desc, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 14px",
        borderRadius: 10, border: `1px solid ${T.border}`, cursor: "pointer",
        transition: "all 0.18s", background: "#fff",
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = T.rose; e.currentTarget.style.background = "#fff8f8"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = "#fff"; }}
    >
      <div style={{ fontSize: 22, lineHeight: 1, flexShrink: 0, marginTop: 2 }}>{icon}</div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 3 }}>{title}</div>
        <div style={{ fontSize: 11, color: T.muted, lineHeight: 1.4 }}>{desc}</div>
      </div>
      <div style={{ marginLeft: "auto", fontSize: 16, color: T.muted, alignSelf: "center", flexShrink: 0 }}>›</div>
    </div>
  );
}

// ── Snapshot Share Sheet ───────────────────────────────────────────
function SnapshotShareSheet({ STAGES, SNAPSHOT_SECTIONS, getStageProgress, userContext, platform, onConfirm, onClose }) {
  const completedSections = SNAPSHOT_SECTIONS
    ? SNAPSHOT_SECTIONS.filter(sec => getStageProgress && getStageProgress(sec.stage)?.done)
    : [];

  // Build default config: all stages & context shared = true
  const [stageConfig, setStageConfig] = useState(() => {
    const cfg = {};
    (completedSections).forEach(sec => {
      cfg[sec.stage] = { share: true, context: true };
    });
    return cfg;
  });
  const [expanded, setExpanded] = useState({});

  const toggleStageShare = (stage) => {
    setStageConfig(prev => ({ ...prev, [stage]: { ...prev[stage], share: !prev[stage].share } }));
  };
  const toggleContextShare = (stage) => {
    setStageConfig(prev => ({ ...prev, [stage]: { ...prev[stage], context: !prev[stage].context } }));
  };
  const toggleExpand = (stage) => {
    setExpanded(prev => ({ ...prev, [stage]: !prev[stage] }));
  };

  const sharedCount = Object.values(stageConfig).filter(v => v.share).length;
  const platformLabel = PLATFORM_ICONS[platform]?.label || platform;

  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 9998, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "flex-end", justifyContent: "center" }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff", borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 560,
          maxHeight: "88vh", display: "flex", flexDirection: "column",
          boxShadow: "0 -8px 40px rgba(0,0,0,0.2)",
          animation: "sheetSlideUp 0.32s cubic-bezier(0.32,0.72,0,1) both",
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Sheet handle */}
        <div style={{ display: "flex", justifyContent: "center", paddingTop: 12, paddingBottom: 4, flexShrink: 0 }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: T.border }} />
        </div>

        {/* Sheet header */}
        <div style={{ padding: "12px 20px 14px", borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: T.text, marginBottom: 4 }}>Choose What to Share</div>
          <div style={{ fontSize: 12, color: T.muted }}>
            {sharedCount} of {completedSections.length} stages will be shared via {platformLabel}
          </div>
        </div>

        {/* Stage accordion list */}
        <div style={{ overflowY: "auto", flex: 1, padding: "8px 0" }}>
          {completedSections.length === 0 && (
            <div style={{ padding: 24, textAlign: "center", color: T.muted, fontSize: 14 }}>
              Complete some interview stages first to share your snapshot.
            </div>
          )}
          {completedSections.map((sec) => {
            const cfg = stageConfig[sec.stage] || { share: true, context: true };
            const stageCtx = (userContext && userContext[sec.stage]) || [];
            const isExpanded = expanded[sec.stage];
            const insight = sec.getInsight ? sec.getInsight() : null;
            const stageName = STAGES?.[sec.stage]?.name || sec.title;
            const stageDesc = STAGES?.[sec.stage]?.desc || "";

            return (
              <div
                key={sec.stage}
                style={{
                  borderBottom: `1px solid ${T.border}`,
                  opacity: cfg.share ? 1 : 0.45,
                  transition: "opacity 0.2s",
                }}
              >
                {/* Stage row */}
                <div style={{ display: "flex", alignItems: "center", padding: "13px 20px", gap: 10 }}>
                  {/* Expand toggle */}
                  <button
                    onClick={() => toggleExpand(sec.stage)}
                    style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, color: T.muted, padding: "0 4px", flexShrink: 0, fontFamily: "inherit" }}
                  >
                    {isExpanded ? "▼" : "▶"}
                  </button>

                  {/* Stage name */}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: cfg.share ? T.text : T.muted }}>{stageName}</div>
                    <div style={{ fontSize: 11, color: T.muted, marginTop: 1 }}>{stageDesc}</div>
                  </div>

                  {/* Share toggle pill */}
                  <ShareToggle
                    on={cfg.share}
                    onToggle={() => toggleStageShare(sec.stage)}
                  />
                </div>

                {/* Expanded content */}
                {isExpanded && (
                  <div style={{ paddingLeft: 44, paddingRight: 20, paddingBottom: 14 }}>
                    {/* Insight preview */}
                    {insight?.content && (
                      <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.6, marginBottom: 10, padding: "8px 12px", background: T.bg, borderRadius: 8 }}>
                        {insight.content.length > 140 ? insight.content.slice(0, 140) + "…" : insight.content}
                      </div>
                    )}

                    {/* Context sub-section */}
                    {stageCtx.length > 0 && (
                      <div style={{ marginTop: 6 }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, letterSpacing: "0.06em" }}>ADDED CONTEXT</div>
                          <ShareToggle
                            on={cfg.context}
                            onToggle={() => toggleContextShare(sec.stage)}
                            small
                          />
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {stageCtx.map((ctx, i) => (
                            <span key={i} style={{
                              fontSize: 11, padding: "3px 10px", borderRadius: 20,
                              background: cfg.context ? "#f0f4ff" : T.bg,
                              color: cfg.context ? "#4f46e5" : T.muted,
                              border: `1px solid ${cfg.context ? "#c7d2fe" : T.border}`,
                              transition: "all 0.2s",
                            }}>{ctx}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer CTA */}
        <div style={{ padding: "14px 20px", borderTop: `1px solid ${T.border}`, flexShrink: 0, background: "#fff" }}>
          <button
            onClick={() => onConfirm(stageConfig)}
            disabled={sharedCount === 0}
            style={{
              width: "100%", padding: "14px 0", borderRadius: 10,
              background: sharedCount > 0 ? `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})` : T.border,
              border: "none", color: "#fff", fontSize: 15, fontWeight: 800,
              cursor: sharedCount > 0 ? "pointer" : "not-allowed", fontFamily: "inherit",
              transition: "all 0.2s",
            }}
          >
            {sharedCount > 0 ? `Share ${sharedCount} Stage${sharedCount > 1 ? "s" : ""} via ${PLATFORM_ICONS[platform]?.label || platform}` : "Select at least one stage"}
          </button>
          <button onClick={onClose} style={{ width: "100%", marginTop: 8, padding: "10px 0", borderRadius: 10, background: "none", border: "none", color: T.muted, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Share Toggle pill ──────────────────────────────────────────────
function ShareToggle({ on, onToggle, small }) {
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      style={{
        display: "flex", alignItems: "center", gap: 5, padding: small ? "3px 8px" : "4px 10px",
        borderRadius: 20, border: `1px solid ${on ? T.rose : T.border}`,
        background: on ? "#fff1f2" : T.bg, cursor: "pointer", fontFamily: "inherit",
        fontSize: small ? 10 : 11, fontWeight: 700, color: on ? T.rose : T.muted,
        transition: "all 0.18s", flexShrink: 0,
      }}
    >
      <div style={{
        width: small ? 8 : 10, height: small ? 8 : 10, borderRadius: "50%",
        background: on ? T.rose : T.border, transition: "background 0.18s",
      }} />
      {on ? "Share" : "Skip"}
    </button>
  );
}

// ── Share Links Settings Panel ─────────────────────────────────────
export function ShareLinksPanel() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [qrLink, setQrLink] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetchShareLinks();
      setLinks(data);
    } catch {
      setLinks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (link) => {
    try {
      await apiToggleShareLink(link.id, !link.is_active);
      setLinks(prev => prev.map(l => l.id === link.id ? { ...l, is_active: !l.is_active } : l));
    } catch {}
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this share link? This cannot be undone.")) return;
    try {
      await apiDeleteShareLink(id);
      setLinks(prev => prev.filter(l => l.id !== id));
    } catch {}
  };

  const handleSaveLabel = async (id) => {
    try {
      await apiUpdateShareLinkLabel(id, editLabel);
      setLinks(prev => prev.map(l => l.id === id ? { ...l, label: editLabel } : l));
      setEditingId(null);
    } catch {}
  };

  if (loading) {
    return (
      <div style={{ padding: "24px 0", textAlign: "center", color: T.muted, fontSize: 14 }}>
        Loading share links…
      </div>
    );
  }

  return (
    <div>
      {links.length === 0 ? (
        <div style={{ textAlign: "center", padding: "28px 0", color: T.muted, fontSize: 13, border: `1px dashed ${T.border}`, borderRadius: 10 }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>🔗</div>
          <div style={{ fontWeight: 600, marginBottom: 3 }}>No share links yet</div>
          <div style={{ fontSize: 11 }}>Use "Share with friends" on your personality snapshot to create one.</div>
        </div>
      ) : (
        <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}>
          {/* Table header */}
          <div style={{ display: "grid", gridTemplateColumns: "28px 1fr 80px 72px 80px 100px", gap: "0 12px", alignItems: "center", padding: "7px 14px", background: T.bg, borderBottom: `1px solid ${T.border}` }}>
            <div />
            <div style={{ fontSize: 10, fontWeight: 700, color: T.muted, letterSpacing: "0.07em" }}>LINK</div>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.muted, letterSpacing: "0.07em", textAlign: "center" }}>VIEWS</div>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.muted, letterSpacing: "0.07em", textAlign: "center" }}>SIGNUPS</div>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.muted, letterSpacing: "0.07em", textAlign: "center" }}>TESTS</div>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.muted, letterSpacing: "0.07em", textAlign: "right" }}>ACTIONS</div>
          </div>

          {links.map((link, idx) => {
            const typeLabel = link.type === "compatibility_test" ? "Compat" : "Snapshot";
            const typeColor = link.type === "compatibility_test" ? T.violet : T.teal;
            const createdDate = new Date(link.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" });
            const isEditing = editingId === link.id;
            const isLast = idx === links.length - 1;

            return (
              <div
                key={link.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "28px 1fr 80px 72px 80px 100px",
                  gap: "0 12px",
                  alignItems: "center",
                  padding: "9px 14px",
                  background: link.is_active ? "#fff" : T.bg,
                  borderBottom: isLast ? "none" : `1px solid ${T.border}`,
                  opacity: link.is_active ? 1 : 0.55,
                  transition: "opacity 0.2s",
                }}
              >
                {/* Platform icon */}
                <PlatformIcon platform={link.platform || "link"} size={28} />

                {/* Label + type + date */}
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 20, background: typeColor + "18", color: typeColor, letterSpacing: "0.04em", flexShrink: 0 }}>
                      {typeLabel}
                    </span>
                    {!link.is_active && (
                      <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 20, background: "#f3f4f6", color: T.muted, flexShrink: 0 }}>OFF</span>
                    )}
                  </div>
                  {isEditing ? (
                    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                      <input
                        value={editLabel}
                        onChange={e => setEditLabel(e.target.value)}
                        style={{ flex: 1, fontSize: 11, padding: "2px 6px", border: `1px solid ${T.rose}`, borderRadius: 5, fontFamily: "inherit", outline: "none", minWidth: 0 }}
                        onKeyDown={e => { if (e.key === "Enter") handleSaveLabel(link.id); if (e.key === "Escape") setEditingId(null); }}
                        autoFocus
                      />
                      <button onClick={() => handleSaveLabel(link.id)} style={{ fontSize: 10, color: T.rose, background: "none", border: "none", cursor: "pointer", fontWeight: 700, fontFamily: "inherit", flexShrink: 0 }}>Save</button>
                      <button onClick={() => setEditingId(null)} style={{ fontSize: 10, color: T.muted, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}>✕</button>
                    </div>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {link.label || `${PLATFORM_META[link.platform]?.label || "Link"} share`}
                      </span>
                      <button
                        onClick={() => { setEditingId(link.id); setEditLabel(link.label || ""); }}
                        title="Rename"
                        style={{ fontSize: 10, color: T.muted, background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "inherit", flexShrink: 0, lineHeight: 1 }}
                      >
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      </button>
                    </div>
                  )}
                  <div style={{ fontSize: 10, color: T.muted }}>{createdDate}</div>
                </div>

                {/* Stats */}
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.text, lineHeight: 1 }}>{link.views ?? 0}</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.text, lineHeight: 1 }}>{link.conversions ?? 0}</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.text, lineHeight: 1 }}>{link.tests_taken ?? 0}</div>
                </div>

                {/* Actions */}
                <div style={{ display: "flex", gap: 4, justifyContent: "flex-end", alignItems: "center" }}>
                  <IconBtn title="Copy link" onClick={() => navigator.clipboard.writeText(link.url)}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                  </IconBtn>
                  <IconBtn title="Show QR code" onClick={() => setQrLink(link)}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="5" y="5" width="3" height="3" fill="currentColor" stroke="none"/><rect x="16" y="5" width="3" height="3" fill="currentColor" stroke="none"/><rect x="5" y="16" width="3" height="3" fill="currentColor" stroke="none"/><path d="M14 14h3v3m3 1v3m-3 0h3" strokeLinecap="round"/></svg>
                  </IconBtn>
                  <IconBtn
                    title={link.is_active ? "Disable" : "Enable"}
                    onClick={() => handleToggle(link)}
                    active={link.is_active}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      {link.is_active
                        ? <><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></>
                        : <><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></>}
                    </svg>
                  </IconBtn>
                  <IconBtn title="Delete" onClick={() => handleDelete(link.id)} danger>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3,6 5,6 21,6"/><path d="M19,6l-1,14a2,2,0,0,1-2,2H8a2,2,0,0,1-2-2L5,6"/><path d="M10,11v6"/><path d="M14,11v6"/><path d="M9,6V4a1,1,0,0,1,1-1h4a1,1,0,0,1,1,1v2"/></svg>
                  </IconBtn>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {qrLink && (
        <QRModal
          url={qrLink.url}
          title={qrLink.type === "compatibility_test" ? "Take a Compatibility Test" : "My Personality Snapshot"}
          onClose={() => setQrLink(null)}
        />
      )}
    </div>
  );
}

function IconBtn({ children, onClick, title, active, danger }) {
  const [hov, setHov] = useState(false);
  const color = danger ? "#ef4444" : active ? T.rose : T.muted;
  return (
    <button
      onClick={onClick}
      title={title}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        width: 26, height: 26, borderRadius: 6, border: `1px solid ${hov ? (danger ? "#ef4444" : T.rose) : T.border}`,
        background: hov ? (danger ? "#fff5f5" : active ? "#fff1f2" : T.bg) : "#fff",
        color: hov ? (danger ? "#ef4444" : T.rose) : color,
        cursor: "pointer", transition: "all 0.15s", flexShrink: 0,
      }}
    >
      {children}
    </button>
  );
}

// ── Join flow helpers ──────────────────────────────────────────────
export function detectShareToken() {
  const params = new URLSearchParams(window.location.search);
  const compat = params.get("compat");
  const snap = params.get("snap");
  if (compat) return { token: compat, type: "compatibility_test" };
  if (snap) return { token: snap, type: "personality_snapshot" };
  return null;
}

export async function fetchShareLinkData(token) {
  const res = await fetch(`${API_BASE}/api/share/${token}`);
  if (!res.ok) throw new Error("Link not found or expired");
  return res.json();
}

export async function recordShareEvent(token, event_type, new_user_id = null) {
  try {
    await fetch(`${API_BASE}/api/share/${token}/event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ event_type, new_user_id }),
    });
  } catch {}
}
